import { EvaChart } from "constants/charts.enums";
import { EChartsOption } from "echarts";
import { EvaChartStates } from "types/factory";
import formatPropertyName from "utils/column/formatPropertyName";
import { applyFieldNameMode } from "utils/column/getColumnLabelWithContext";

import { translateRateCumResponseToOptions } from ".";
import { getAxisFieldProperties } from "./axisFieldProperties";
import { translateBoxPlotResponseToOptions } from "./eva/translators/boxPlot";
import { translateCAGRResponseToOptions } from "./eva/translators/cagr";
import { translateCrossPlotResponseToOptions } from "./eva/translators/crossPlot";
import { translateCumTimeResponseToOptions } from "./eva/translators/cumTime";
import { translateDeclineRateResponseToOptions } from "./eva/translators/declineRate";
import { translateInjectorTotalResponseToOptions } from "./eva/translators/injectorTotal";
import { translateMaterialBalanceTimeResponseToOptions } from "./eva/translators/materialBalanceTime";
import { translateMosaicResponseToOptions } from "./eva/translators/mosaic";
import { translatePieResponseToOptions } from "./eva/translators/pie";
import { translateProbitResponseToOptions } from "./eva/translators/probit";
import { translateRateDateResponseToOptions } from "./eva/translators/rateDate";
import { translateRateTimeResponseToOptions } from "./eva/translators/rateTime";
import { translateRecoveryPVIResponseToOptions } from "./eva/translators/recoveryPVI";
import { translateRecoveryTimeResponseToOptions } from "./eva/translators/recoveryTime";
import { translateRigFracActivityResponseToOptions } from "./eva/translators/rigFracActivity";
import { translateRigFracRecentActivityResponseToOptions } from "./eva/translators/rigFracRecentActivity";
import { translateStackedBarResponseToOptions } from "./eva/translators/stackedBar";
import { translateStackedBarHorizontalResponseToOptions } from "./eva/translators/stackedBarHorizontal";
import { translateTotalRateCumResponseToOptions } from "./eva/translators/totalRateCum";
import { translateTotalRateDateResponseToOptions } from "./eva/translators/totalRateDate";
import { translateTrendDateResponseToOptions } from "./eva/translators/trendDate";
import { translateWellContributionResponseToOptions } from "./eva/translators/wellContribution";
import { translateWellStatusCurrentResponseToOptions } from "./eva/translators/wellStatusCurrent";
import { translateWellStatusOverTimeResponseToOptions } from "./eva/translators/wellStatusOverTime";

export const TRANSLATOR_MAP = {
  [EvaChart.RateCum]: translateRateCumResponseToOptions,
  [EvaChart.RecoveryPVI]: translateRecoveryPVIResponseToOptions,
  [EvaChart.RecoveryTime]: translateRecoveryTimeResponseToOptions,
  [EvaChart.CrossPlot]: translateCrossPlotResponseToOptions,
  [EvaChart.CumTime]: translateCumTimeResponseToOptions,
  [EvaChart.RateTime]: translateRateTimeResponseToOptions,
  [EvaChart.Pie]: translatePieResponseToOptions,
  [EvaChart.RateDate]: translateRateDateResponseToOptions,
  [EvaChart.TotalRateDate]: translateTotalRateDateResponseToOptions,
  [EvaChart.TotalRateCum]: translateTotalRateCumResponseToOptions,
  [EvaChart.DeclineRate]: translateDeclineRateResponseToOptions,
  [EvaChart.CAGR]: translateCAGRResponseToOptions,
  [EvaChart.TrendDate]: translateTrendDateResponseToOptions,
  [EvaChart.Mosaic]: translateMosaicResponseToOptions,
  [EvaChart.StackedBar]: translateStackedBarResponseToOptions,
  [EvaChart.StackedBarHorizontal]: translateStackedBarHorizontalResponseToOptions,
  [EvaChart.BoxPlot]: translateBoxPlotResponseToOptions,
  [EvaChart.Probit]: translateProbitResponseToOptions,
  [EvaChart.MaterialBalanceTime]: translateMaterialBalanceTimeResponseToOptions,
  [EvaChart.InjectorTotal]: translateInjectorTotalResponseToOptions,
  [EvaChart.RigFracActivity]: translateRigFracActivityResponseToOptions,
  [EvaChart.RigFracRecentActivity]: translateRigFracRecentActivityResponseToOptions,
  // Well Status widget. useWellStatusChartOptions calls these directly,
  // but they are registered here too so any shared caller that routes by
  // chartType (screenshot, data table) resolves instead of throwing.
  [EvaChart.WellStatusOverTime]: translateWellStatusOverTimeResponseToOptions,
  [EvaChart.WellStatusCurrent]: translateWellStatusCurrentResponseToOptions,

  // midstream
  [EvaChart.RateDateMidstream]: translateRateDateResponseToOptions,
  [EvaChart.TotalRateDateMidstream]: translateTotalRateDateResponseToOptions,
  [EvaChart.TrendDateMidstream]: translateTrendDateResponseToOptions,
  [EvaChart.CrossPlotMidstream]: translateCrossPlotResponseToOptions,
  [EvaChart.WellContribution]: translateWellContributionResponseToOptions,
  [EvaChart.MosaicMidstream]: translateMosaicResponseToOptions,
  [EvaChart.StackedBarMidstream]: translateStackedBarResponseToOptions,
  [EvaChart.BoxPlotMidstream]: translateBoxPlotResponseToOptions
};

/**
 * Qualifies a single echarts axis title (`.name`) with the field's radius/
 * source/name/group/test context so the rendered axis label matches the focus
 * panel and legend. Only touches a single-object axis with a string name; the
 * decorator no-ops for field types that don't carry extra context. Runs on the
 * echarts `name` only — never the response's `layout.*.title`, which the render
 * hook parses for units/axis-type — so it can't regress data.
 */
export const qualifyAxisName = (
  axis: EChartsOption["xAxis"],
  fieldProperty: Maybe<string>,
  columnsDictionary?: Record<string, EvaChartStates["columnsDictionary"][string]>,
  // EVA-127 "Simplify Custom Field Names" screenshot toggle. `undefined` means "not in
  // screenshot mode"; `true`/`false` are the toggle's resolved ON/OFF state — the
  // caller must already have applied `?? false` before passing this in. All three
  // cases are interpreted by applyFieldNameMode; `undefined` and `false` behave
  // identically today.
  simplifyCustomFieldNames?: Maybe<boolean>
): void => {
  if (!axis || Array.isArray(axis) || typeof axis.name !== "string") return;
  if (!fieldProperty || !columnsDictionary) return;
  const column = columnsDictionary[formatPropertyName(fieldProperty)];
  if (!column) return;

  axis.name = applyFieldNameMode(axis.name, column, simplifyCustomFieldNames);
};

export function translateToOptions({
  type,
  states
}: {
  type: EvaChart;
  states: Partial<EvaChartStates>;
}): EChartsOption {
  const translator = TRANSLATOR_MAP[type];
  if (!translator) {
    throw new Error(`Unknown chart type: ${type} to translate`);
  }
  const options = translator(states);

  // Match the axis label to what the focus panel / legend show for the field.
  const axisFields = getAxisFieldProperties(type, states.chartTypeParam, {
    jitterActive: states.jitterPlotToggle?.active,
    violinActive: states.violinPlotToggle?.active
  });
  qualifyAxisName(
    options.xAxis,
    axisFields.x,
    states.columnsDictionary,
    states.simplifyCustomFieldNames
  );
  qualifyAxisName(
    options.yAxis,
    axisFields.y,
    states.columnsDictionary,
    states.simplifyCustomFieldNames
  );

  return options;
}
